import { Flex, Skeleton } from '@chakra-ui/react';
import { fieldValidationMessages } from 'app/constants';
import { buildQueryUrl } from 'app/utils/utils';
import { useCallback, useState } from 'react';
import { ReactComponent as SearchSvg } from 'resources/images/search.svg';
import { ChakraAutocompleteProps } from 'app/components/Core/ChakraAutocomplete/types';
import { Link } from 'types/api.types';
import { ChakraAutocomplete } from '../ChakraAutocomplete/ChakraAutocomplete';
import { useChakraAsyncAutocompleteSearch } from './utils';

export interface ChakraAsyncAutocompleteProps<O extends {}, R>
  extends ChakraAutocompleteProps<O> {
  getResponseItems: (response: R, data: O[]) => O[];
  getNextResponseLink?: (response: R) => Link;
  maxSearchLength?: number;
  fetchCallback?: (value: string) => void;
  searchURL: string;
  loadingSkeleton?: React.ReactNode;
  initialOption?: O;
}

export const ChakraAsyncAutocomplete = <O extends {}, R extends {}>({
  getResponseItems,
  getNextResponseLink,
  maxSearchLength,
  fetchCallback,
  searchURL,
  loadingSkeleton,
  inputValue: inputValueProp = '',
  initialOption,
  onInputChange,
  components,
  ...rest
}: ChakraAsyncAutocompleteProps<O, R>) => {
  const [inputValue, setInputValue] = useState(inputValueProp);
  const {
    fetch,
    options,
    loading,
    clearOptions,
  } = useChakraAsyncAutocompleteSearch<O, R>(
    getResponseItems,
    fetchCallback,
    getNextResponseLink
  );

  const isInputEmpty = inputValue.trim() === '';

  const maxLengthError =
    maxSearchLength && inputValue.length > maxSearchLength
      ? fieldValidationMessages.MAX_CHAR(maxSearchLength)
      : '';

  const shouldForceCloseMenu =
    (isInputEmpty && !initialOption) || maxLengthError;

  const handleInputChange = useCallback(
    (value: string) => {
      setInputValue(value);
      if (
        value === '' ||
        (maxSearchLength && value.trim().length > maxSearchLength)
      ) {
        clearOptions();
      } else if (value.trim() && searchURL) {
        const url = buildQueryUrl(searchURL, {
          search: value,
        });
        fetch(url);
      }
    },
    [clearOptions, fetch, maxSearchLength, searchURL]
  );

  return (
    <ChakraAutocomplete<O>
      {...rest}
      components={components}
      onInputChange={(value, meta) => {
        handleInputChange(value);
        if (onInputChange) {
          onInputChange(value, meta);
        }
      }}
      options={isInputEmpty && initialOption ? [initialOption] : options}
      menuIsOpen={shouldForceCloseMenu ? false : undefined}
      filterOption={() => true}
      renderLoadingIndicator={false}
      isLoading={loading}
      customComponentsProps={{
        inputLeftElement: <SearchSvg height="16px" width="17px" />,
      }}
      isInvalid={Boolean(maxLengthError)}
      errorMessage={maxLengthError}
      loadingMessage={() =>
        loadingSkeleton || (
          <Flex height={29} alignItems="center" px={4}>
            <Skeleton height={18} width={80} />
          </Flex>
        )
      }
    />
  );
};
